import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useTRPC } from '../../lib/trpc'
import { BOOK_POLL_MS, FLASH_MS, keepPlace, mayApply } from './live-text'

/**
 * The reading itself, which is the only place these marks belong.
 *
 * `data-block` is not the reader's alone — the comments view carries the same
 * attribute for the passage each thread is about — so a lookup across the whole
 * document can answer with a band element instead. That would mark a passage
 * where no rule paints it, and worse, correct the reader's scroll against
 * something in a band that scrolls separately.
 */
const reading = () => document.querySelector('.reader')

const blockIn = (id: string) =>
  reading()?.querySelector<HTMLElement>(`[data-block="${CSS.escape(id)}"]`) ??
  null

/**
 * A reading that stays current.
 *
 * A book composed once at page load says what it said then, so somebody reading
 * beside whoever is writing is reading something nobody else can see. The
 * reader asks what has been committed, takes the new words, and the passages
 * that were saved flash long enough to be found.
 *
 * **The server says which passages.** An edit is an event and the book already
 * records it, so there is nothing for the reader to work out — and because the
 * answer comes from the log it carries the actor, which is what lets somebody's
 * own writing be left out before it is ever sent.
 *
 * **The mark is written to the DOM rather than held in React state.** It is a
 * transient decoration on a passage, not a fact about the book — putting it in
 * state would re-render the whole composed tree twice per change to add and
 * remove an attribute, on the one screen where a re-render costs the most. The
 * attribute is the same `data-` vocabulary every other state here publishes.
 *
 * Nothing about any of this is stored. What a reader is caught up to lives in
 * this hook for as long as the tab is open and nowhere else, which is what
 * keeps a live reading from becoming a record of one.
 */
export function useLiveText({
  bookId,
  token,
  openBlockId,
  dataUpdatedAt,
}: {
  /** The book, for a reader who is signed in. */
  bookId?: string
  /** The book, for a reading copy, where the token is the whole authorization. */
  token?: string
  /** The passage this reader has open for writing, if any. */
  openBlockId: string | null
  /**
   * When the book this reader is holding last actually arrived.
   *
   * The signal is the words LANDING, never the request being made: asking a
   * query to refetch resolves whether it fetched, was paused offline, or was
   * skipped for being inactive — so a page that received nothing would
   * otherwise mark passages in their old wording, and then reflow unannounced
   * whenever the real answer turned up.
   */
  dataUpdatedAt: number
}) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  /**
   * Asked only while somebody is looking at the answer.
   *
   * A tab in the background is not reading, and a book left open on a phone in
   * a pocket should cost nothing at all. Coming back asks at once, which is also
   * what makes a long absence cheap: one question, however many saves.
   */
  const [awake, setAwake] = useState(() => !document.hidden)
  useEffect(() => {
    const onVisibility = () => setAwake(!document.hidden)
    document.addEventListener('visibilitychange', onVisibility)
    return () => document.removeEventListener('visibilitychange', onVisibility)
  }, [])

  /**
   * Where in the book's log this reader is caught up to.
   *
   * Absent on the first ask, which is what makes arriving quiet: everything in
   * a book somebody just opened is new to them, so the first answer reports the
   * cursor and names no passages.
   */
  const [since, setSince] = useState<string | undefined>(undefined)

  const signedIn = bookId !== undefined
  const probe = useQuery({
    ...(signedIn
      ? trpc.share.changedSince.queryOptions({
          bookId: bookId as string,
          since,
        })
      : trpc.share.changedSinceByToken.queryOptions({
          token: token as string,
          since,
        })),
    enabled: awake && (signedIn ? !!bookId : !!token),
    refetchInterval: awake ? BOOK_POLL_MS : false,
  })

  /** Passages saved since this reader last caught up, waiting to be drawn. */
  const pending = useRef<string[]>([])
  /** That an update of ours is on its way into the page. */
  const applying = useRef(false)
  /** What carries a mark now, so it can be taken back off. */
  const marked = useRef<HTMLElement[]>([])
  /** Where the reader was, taken before the new words displaced them. */
  const anchor = useRef<{ id: string; top: number } | null>(null)

  /**
   * A different book is a different reading.
   *
   * The route re-renders rather than remounting when one book opens
   * another, so all of this would otherwise carry across: the first ask for the
   * second book would arrive holding the first book's cursor and be answered
   * with every edit made while the reader was elsewhere, which is the opposite
   * of arriving quietly. The passages owed a mark belong to the other book and
   * mean nothing here.
   */
  const held = useRef(signedIn ? bookId : token)
  const opened = signedIn ? bookId : token
  useEffect(() => {
    if (held.current === opened) return
    held.current = opened
    pending.current = []
    applying.current = false
    setSince(undefined)
  }, [opened])

  const answered = probe.data
  useEffect(() => {
    if (!answered) return
    const { cursor } = answered
    if (cursor === null || cursor === since) return
    /* Whether this reader may take it now is a separate question: a passage
       open for writing holds the page until it closes. */
    if (!mayApply(openBlockId)) return

    /*
     * Measured HERE, while the words on screen are still the old ones. Taken
     * from scroll events instead, a reader who never scrolled — one who
     * reloaded partway through a book, whose position the browser restored —
     * would have nothing to hold their place against.
     */
    const first = Array.from(
      reading()?.querySelectorAll<HTMLElement>('[data-block]') ?? [],
    ).find((node) => node.getBoundingClientRect().bottom > 0)
    const at = first?.getAttribute('data-block')
    anchor.current =
      at && first ? { id: at, top: first.getBoundingClientRect().top } : null

    /* Added to rather than replaced: two answers can land before the words are
       drawn, and the earlier one's passages are still owed a mark. */
    for (const blockId of answered.blocks) {
      if (!pending.current.includes(blockId)) pending.current.push(blockId)
    }
    applying.current = true
    setSince(cursor)
    void queryClient.invalidateQueries({
      queryKey: signedIn
        ? trpc.share.book.queryKey({ bookId: bookId as string })
        : trpc.share.read.queryKey({ token: token as string }),
    })
    /*
     * The remarks go with the words. A comment is anchored by the phrase it
     * quotes, so a book whose passages moved under a cached thread resolves that
     * quote against text it no longer matches, and the mark in the margin lands
     * wrongly or not at all.
     */
    if (signedIn) {
      void queryClient.invalidateQueries({
        queryKey: trpc.share.comments.queryKey({
          bookId: bookId as string,
        }),
      })
    }
  }, [answered, since, openBlockId, queryClient, trpc, signedIn, bookId, token])

  /**
   * The new words drawn: the page held where it was, and what was saved marked.
   *
   * A layout effect, because both have to happen before the reader sees a
   * frame — a correction they can see is a jump, and a mark a frame late is a
   * flicker.
   */
  useLayoutEffect(() => {
    /* Read rather than merely depended on: this runs when words arrive, and the
       moment they arrived is what says they did. */
    if (dataUpdatedAt === 0) return
    if (!applying.current) return
    applying.current = false
    const marking = pending.current
    pending.current = []

    /*
     * Whatever a previous update left marked comes off first. An attribute left
     * on a passage is not merely untidy: setting one that is already there
     * restarts nothing, so that passage would never flash again — and a reader
     * who asked for less motion, whose mark is a held fill rather than a fade,
     * would be left with it shaded for good.
     */
    for (const node of marked.current) node.removeAttribute('data-changed')
    marked.current = []

    /*
     * The place is held whatever arrived, before anything is marked. A change
     * that names no passage — a part renamed, a note, the book's own title —
     * still reflows everything under it, and those are the updates a reader has
     * no way at all to account for.
     */
    const anchored = anchor.current
    if (anchored) {
      const node = blockIn(anchored.id)
      /* A passage that went with the change cannot hold anything. Leaving the
         page alone is the only honest answer — correcting against a different
         passage would move the reader deliberately. */
      if (node) {
        const drift = keepPlace(anchored.top, node.getBoundingClientRect().top)
        if (drift !== 0) window.scrollBy(0, drift)
      }
    }

    if (marking.length === 0) return
    const now = marking
      .map(blockIn)
      .filter((node): node is HTMLElement => node !== null)
    for (const node of now) {
      /* The stylesheet fades for exactly as long as the mark is held, so the
         two cannot come to different answers about how long a flash is. */
      node.style.setProperty('--flash-ms', `${FLASH_MS}ms`)
      node.setAttribute('data-changed', '')
    }
    marked.current = now
    const timer = setTimeout(() => {
      for (const node of now) node.removeAttribute('data-changed')
      marked.current = []
    }, FLASH_MS)
    return () => clearTimeout(timer)
  }, [dataUpdatedAt])
}
